import React from 'react';
import styled from 'styled-components';
import {Checkbox, Detail, SubHeading, Text} from '@forecast-it/design-system';
import {FEATURE_FLAG_CARD_BORDER, FEATURE_FLAG_NEUTRAL_FILL, FEATURE_FLAG_WARNING_TEXT} from '../featureFlagSurface';
import {RolloutCandidate} from './featureFlagRolloutDirectory';

// Shared by the header and the rows so the two cannot drift apart, matching how the assignment table
// shares its grid. The always-last column is a fixed width rather than sized to its content, so rows
// line up whether or not any company in this particular preview is on the list — and so the columns do
// not shift as the operator moves between tabs.
const PREVIEW_COLUMNS = '24px 1fr 74px 96px';
const PREVIEW_GAP = '12px';

const PreviewCard = styled.div`
	border: 1px solid ${FEATURE_FLAG_CARD_BORDER};
	border-radius: 4px;
	overflow: hidden;
`;

const PreviewHead = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
	padding: 10px 14px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

// No height cap and no scroll region of its own — Modal.Content is the single scroll container, so a
// full rollout scrolls the whole modal body rather than a second box nested inside it. The minimum
// below is what keeps the dialog from visibly resizing as rows come and go: without it, an empty or
// short list would let the card collapse to a sliver and the dialog would jump taller the moment a row
// appears. Sized off the viewport, not a flat pixel value, so a short window gets a smaller reservation
// instead of forcing itself past what the window can show.
const PreviewBody = styled.div`
	min-height: min(240px, 30vh);
`;

const PreviewRow = styled.div`
	display: grid;
	grid-template-columns: ${PREVIEW_COLUMNS};
	gap: ${PREVIEW_GAP};
	align-items: center;
	min-height: 42px;
	padding: 0 14px;
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};

	&:last-child {
		border-bottom: none;
	}
`;

const NameCell = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const CountCell = styled.span`
	display: flex;
	justify-content: flex-end;
`;

/*
 * The always-last marker. `Detail` accepts no warning colour, so the colour is applied to the element
 * Detail actually renders — the same override the manage-lists membership table uses for the same
 * marker, so one company reads the same way on both screens.
 */
const AlwaysLastCell = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	> * {
		color: ${FEATURE_FLAG_WARNING_TEXT};
	}
`;

const EmptyPreview = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	padding: 36px 20px;
	text-align: center;
`;

export interface RolloutPreviewTableProps {
	/** In fill order. Rendered in the order given. */
	candidates: readonly RolloutCandidate[];
	/**
	 * Companies the operator has unchecked. Tracking who is held back rather than who is selected
	 * means a company that appears when the target is raised is included by default.
	 */
	heldBackCompanyIds: Set<string>;
	onToggleCompany: (companyId: string) => void;
	/** What to say when there is nothing to add — the reason differs per tab. */
	emptyHint: string;
}

export function RolloutPreviewTable({
	candidates,
	heldBackCompanyIds,
	onToggleCompany,
	emptyHint,
}: RolloutPreviewTableProps): JSX.Element {
	const selectedCount = candidates.filter(candidate => !heldBackCompanyIds.has(candidate.companyId)).length;
	const heading =
		selectedCount === 0
			? 'Companies to add'
			: selectedCount === 1
			? '1 company will be added'
			: `${selectedCount} companies will be added`;
	const subheading =
		candidates.length === 0 ? 'Preview is empty' : `Uncheck to hold a company back · ${candidates.length} matched`;

	return (
		<PreviewCard>
			<PreviewHead>
				<SubHeading>{heading}</SubHeading>
				<Detail color="medium">{subheading}</Detail>
			</PreviewHead>
			<PreviewBody>
				{candidates.length === 0 ? (
					<EmptyPreview>
						<Text>Nothing to add yet</Text>
						<Detail color="medium">{emptyHint}</Detail>
					</EmptyPreview>
				) : (
					candidates.map(candidate => (
						<PreviewRow data-cy="rollout-preview-row" key={candidate.companyId}>
							<Checkbox
								aria-label={`Add ${candidate.companyName}`}
								checked={!heldBackCompanyIds.has(candidate.companyId)}
								onChange={() => onToggleCompany(candidate.companyId)}
							/>
							<NameCell>
								<Text>{candidate.companyName}</Text>
							</NameCell>
							<AlwaysLastCell>
								{candidate.alwaysLast ? <Detail color="medium">Always last</Detail> : null}
							</AlwaysLastCell>
							<CountCell>
								<Detail color="medium">
									{candidate.userCount === 1 ? '1 user' : `${candidate.userCount} users`}
								</Detail>
							</CountCell>
						</PreviewRow>
					))
				)}
			</PreviewBody>
		</PreviewCard>
	);
}
